import { useCallback } from 'react';
import { useDraftStore } from '../stores/draftStore';
import { useSettingsStore } from '../stores/settingsStore';
import { generateDraft as apiGenerateDraft } from '../services/api';
import { getEmailItemId, getEmailBodyText, getEmailSubject, getEmailSender, displayReplyWithDraft } from '../services/office';
import { getSupportedAttachmentsWithContent, EwsUnsupportedError } from '../services/attachment';
import type { ToneType, AttachmentData } from '../types/index';

interface UseDraftGenerationResult {
  generateDraft: (options?: { tone?: ToneType; templateId?: string }) => Promise<void>;
  insertDraft: () => Promise<void>;
  discardDraft: () => void;
  isGenerating: boolean;
  error: string | null;
}

export function useDraftGeneration(): UseDraftGenerationResult {
  const {
    draft,
    isGenerating,
    error,
    setDraft,
    setAnalysis,
    setSummary,
    setIsGenerating,
    setError,
    setAttachmentStatus,
    setAttachmentCount,
    setSkippedAttachments,
    setAttachmentWarning,
    clearDraft,
    selectedTone,
  } = useDraftStore();

  const { defaultTone } = useSettingsStore();

  const generateDraft = useCallback(
    async (options?: { tone?: ToneType; templateId?: string }) => {
      const emailId = getEmailItemId();
      if (!emailId) {
        setError('No email selected');
        return;
      }

      setIsGenerating(true);
      setError(null);
      setAttachmentStatus('idle');
      setAttachmentCount(0);
      setSkippedAttachments([]);
      setAttachmentWarning(null);

      try {
        // Get email content to send to API
        const bodyText = await getEmailBodyText();
        const subject = getEmailSubject() || '(No subject)';
        const sender = getEmailSender();

        if (!sender) {
          throw new Error('Could not get email sender information');
        }

        // Fetch attachments
        let attachments: AttachmentData[] = [];
        try {
          setAttachmentStatus('loading');
          const attachmentResult = await getSupportedAttachmentsWithContent();
          attachments = attachmentResult.attachments;
          setAttachmentCount(attachments.length);
          setSkippedAttachments(attachmentResult.skipped);
          setAttachmentStatus(attachments.length > 0 ? 'loaded' : 'idle');
        } catch (attachmentError) {
          console.warn('Failed to load attachments:', attachmentError);
          setAttachmentStatus('error');
          // Show warning for any attachment error - most likely indicates personal account or EWS issue
          const errorName = attachmentError instanceof Error ? attachmentError.name : '';
          const isEwsError = attachmentError instanceof EwsUnsupportedError || errorName === 'EwsUnsupportedError';
          if (isEwsError) {
            setAttachmentWarning('Attachment analysis requires Microsoft 365 Business. Generating draft from email text only.');
          } else {
            // Generic warning for other attachment errors
            setAttachmentWarning('Could not load attachments. Generating draft from email text only.');
          }
          // Continue without attachments
        }

        // Determine which tone to use
        const tone = options?.tone || selectedTone || defaultTone;

        // Call the API with full email content and attachments
        const response = await apiGenerateDraft({
          emailId,
          emailFrom: `${sender.name} <${sender.email}>`,
          emailSubject: subject,
          emailBody: bodyText,
          tone,
          templateId: options?.templateId,
          attachments: attachments.length > 0 ? attachments : undefined,
        });

        // Update store with response
        setDraft(response.draft);
        setAnalysis(response.analysis);
        setSummary(response.summary);
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to generate draft';
        setError(message);
      } finally {
        setIsGenerating(false);
      }
    },
    [selectedTone, defaultTone, setDraft, setAnalysis, setSummary, setIsGenerating, setError, setAttachmentStatus, setAttachmentCount, setSkippedAttachments, setAttachmentWarning]
  );

  const insertDraft = useCallback(async () => {
    if (!draft) {
      setError('No draft to insert');
      return;
    }

    try {
      // Format the draft body as HTML
      const htmlBody = draft.body
        .split('\n')
        .map((line) => (line.trim() ? `<p>${line}</p>` : '<br>'))
        .join('');

      // Display the reply form with the draft content
      await displayReplyWithDraft(htmlBody);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to insert draft';
      setError(message);
    }
  }, [draft, setError]);

  const discardDraft = useCallback(() => {
    clearDraft();
  }, [clearDraft]);

  return {
    generateDraft,
    insertDraft,
    discardDraft,
    isGenerating,
    error,
  };
}
